import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from './App';
import { ChevronUp, ChevronDown, Close, Warning, ArrowRight } from './Icons';

const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];


const SRA_ACCOUNTS_LINKS = [
  { title: 'SRA Accounts Rules 2019 (live)', url: 'https://www.sra.org.uk/solicitors/standards-regulations/accounts-rules/', desc: 'The full authoritative rules — always current' },
  { title: 'SRA — Keeping client money safe', url: 'https://www.sra.org.uk/solicitors/guidance/keeping-client-money-safe/', desc: 'Guidance on client account obligations' },
  { title: 'SRA — Reporting concerns about client money', url: 'https://www.sra.org.uk/consumers/problems/report-solicitor/', desc: 'SRA reporting framework' },
  { title: 'SRA Standards & Regulations hub', url: 'https://www.sra.org.uk/solicitors/standards-regulations/', desc: 'Full standards and regulations index' },
];

function SRALinks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sra-links-section">
      <button className="history-toggle" onClick={()=>setOpen(o=>!o)}>
        {open?<ChevronUp size={10} />:<ChevronDown size={10} />} SRA source materials — official links
      </button>
      {open&&(
        <div className="sra-links-list">
          <div className="sra-links-warning">
            These link directly to the SRA website — always the current version. Do not rely on notes or third-party summaries for rules that may have changed.
          </div>
          {SRA_ACCOUNTS_LINKS.map(l=>(
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="sra-link-item">
              <span className="sra-link-title">{l.title}</span>
              <span className="sra-link-desc">{l.desc}</span>
              <span className="sra-link-arrow"><ArrowRight size={12} /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function confColor(c) {
  if (c===null||c===undefined) return 'var(--muted)';
  if (c<0.5) return 'var(--red)';
  if (c<0.7) return 'var(--amber)';
  return 'var(--green)';
}

function Badge({ c, status }) {
  if (c===null&&!status) return <span className="badge badge-none">Not started</span>;
  if (c===null) return <span className="badge badge-amber">In progress</span>;
  if (c<0.5) return <span className="badge badge-red">Needs work</span>;
  if (c<0.7) return <span className="badge badge-amber">Moderate</span>;
  return <span className="badge badge-green">Strong</span>;
}


function HistoryLog({ topicId }) {
  const [history, setHistory] = useState(null);
  useEffect(()=>{ api(`/api/topics/${topicId}/history`).then(setHistory); },[topicId]);
  if (!history) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading…</div>;
  if (history.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No history yet</div>;
  return (
    <div className="history-log">
      {history.map(h=>(
        <div key={h.id} className="history-row">
          <span className="history-date">{(() => {
            try {
              const d = new Date(h.changed_at.replace(' ', 'T'));
              return d.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) + ' ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
            } catch { return h.changed_at; }
          })()}</span>
          <span className="history-status">{h.status||'—'}</span>
          <span className="history-conf" style={{color:confColor(h.confidence)}}>
            {h.confidence!==null?Math.round(h.confidence*100)+'%':'—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function MCQHistory({ topicId }) {
  const [results, setResults] = useState(null);
  useEffect(()=>{
    api('/api/mcq-results').then(all=>{
      setResults(all.filter(r=>r.topic_id===topicId));
    });
  },[topicId]);

  if (!results) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading…</div>;
  if (results.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No MCQ results logged for this topic yet. Import a BARBRI or Law Drills session to see results here.</div>;

  // Calculate overall from score_pct where available, else from counts
  const withPct = results.filter(r => r.score_pct != null);
  const overallPct = withPct.length > 0
    ? Math.round(withPct.reduce((s,r) => s + r.score_pct, 0) / withPct.length)
    : results.some(r => r.attempted > 0)
      ? Math.round(results.reduce((s,r) => s + r.correct, 0) / results.reduce((s,r) => s + r.attempted, 0) * 100)
      : null;
  const overallColor = overallPct == null ? 'var(--muted)' : overallPct>=70?'var(--green)':overallPct>=50?'var(--amber)':'var(--red)';

  return (
    <div className="mcq-history">
      <div className="mcq-history-summary">
        <span style={{fontSize:12,color:'var(--muted)'}}>Overall accuracy: </span>
        <span style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:500,color:overallColor}}>
          {overallPct != null ? overallPct + '%' : '—'}
        </span>
        <span style={{fontSize:11,color:'var(--muted)'}}> across {results.length} session{results.length!==1?'s':''}</span>
      </div>
      <div className="mcq-history-rows">
        {results.map(r=>{
          const pct = r.score_pct != null ? r.score_pct : (r.attempted > 0 ? Math.round(r.correct/r.attempted*100) : null);
          const c = pct == null ? 'var(--muted)' : pct>=70?'var(--green)':pct>=50?'var(--amber)':'var(--red)';
          const dateStr = (() => { try { return new Date(r.session_date).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}); } catch { return r.session_date; } })();
          return (
            <div key={r.id} className="mcq-history-row" style={{gridTemplateColumns:'90px 1fr 44px 1fr'}}>
              <span className="mcq-history-date">{dateStr}</span>
              <div className="mcq-history-bar-wrap">
                <div className="mcq-history-bar" style={{width:(pct||0)+'%',background:c}}/>
              </div>
              <span className="mcq-history-pct" style={{color:c}}>{pct != null ? pct+'%' : '—'}</span>
              {r.sub_subtopic && r.sub_subtopic !== r.topic_name ? (
                <span style={{fontSize:10,color:'var(--muted)',fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {r.source}: "{r.sub_subtopic}"
                </span>
              ) : (
                <span style={{fontSize:10,color:'var(--hint)'}}>{r.source}</span>
              )}
            </div>
          );
        })}
      </div>
      {results.some(r => r.score_pct != null && r.score_pct < 70) && (
        <div className="mcq-history-weak-note">
          <Warning size={11} /> Some sessions below 70% — focus recall practice on this topic
        </div>
      )}
    </div>
  );
}

function TopicRow({ topic, onSave, forceOpen, moduleColor, isFocused, statuses }) {
  const [open, setOpen] = useState(false);
  const [conf, setConf] = useState(topic.confidence!==null?Math.round(topic.confidence*100):50);
  const [hasConf, setHasConf] = useState(topic.confidence!==null);
  const [status, setStatus] = useState(topic.status||'');
  const [notes, setNotes] = useState(topic.notes||'');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMCQHistory, setShowMCQHistory] = useState(true);
  const saveTimer = useRef(null);
  const rowRef = useRef(null);

  useEffect(()=>{ if(isFocused){ setOpen(true); setTimeout(()=>rowRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),100); } },[isFocused]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  const save = useCallback(async(confVal,hasC,statusVal,notesVal)=>{
    setSaving(true);
    const confNum = hasC?Math.min(100,Math.max(0,parseInt(confVal)))/100:null;
    await api(`/api/topics/${topic.id}`,{method:'PATCH',body:JSON.stringify({
      confidence:(isNaN(confNum)||confNum===null)?null:confNum,
      status:statusVal,notes:notesVal,
      date_updated:new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}).replace(/\//g,'.'),
    })});
    setSaving(false); setSaved(true);
    setTimeout(()=>setSaved(false),1500);
    onSave&&onSave(topic.id,{confidence:(isNaN(confNum)||confNum===null)?null:confNum,status:statusVal,notes:notesVal});
  },[topic.id,onSave]);

  const autoSave=(cVal,hasC,sVal,nVal)=>{
    clearTimeout(saveTimer.current);
    saveTimer.current=setTimeout(()=>save(cVal,hasC,sVal,nVal),600);
  };

  const clearConfidence=async()=>{
    await api(`/api/topics/${topic.id}/clear-confidence`,{method:'POST'});
    setHasConf(false); setConf(50);
    onSave&&onSave(topic.id,{confidence:null});
  };

  const focusRing=isFocused?{boxShadow:`0 0 0 2px ${moduleColor}`,borderColor:moduleColor}:{};

  return (
    <div ref={rowRef} className={`topic-row ${open?'open':''}`} style={focusRing}>
      <div className="topic-header" onClick={()=>setOpen(o=>!o)}>
        <div className="topic-info">
          <div className="topic-name">{topic.subtopic}</div>
          <div className="topic-meta">{topic.general_topic}</div>
        </div>
        <div className="topic-right">
          <Badge c={topic.confidence} status={topic.status}/>
          <div className="conf-wrap">
            <div className="conf-bar-bg"><div className="conf-bar-fill" style={{width:(topic.confidence!==null?Math.round(topic.confidence*100):0)+'%',background:confColor(topic.confidence)}}/></div>
            <span className="conf-pct" style={{color:confColor(topic.confidence)}}>{topic.confidence!==null?Math.round(topic.confidence*100)+'%':'—'}</span>
          </div>
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>

      {open&&(
        <div className="topic-expand" onClick={e=>e.stopPropagation()}>
          {topic.detail&&<div className="topic-detail">{topic.detail}</div>}
          <div className="expand-fields">
            <div className="field-group">
              <label className="field-label">Status</label>
              <select className="field-select" value={status} onChange={e=>{setStatus(e.target.value);autoSave(conf,hasConf,e.target.value,notes);}}>
                <option value="">— select —</option>
                {(statuses||DEFAULT_STATUSES).map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field-group">
              <label className="field-label" style={{display:'flex',alignItems:'center',gap:8}}>
                Confidence
                {hasConf&&<span style={{color:confColor(conf/100),fontFamily:'var(--mono)',fontSize:13}}>{conf}%</span>}
                {hasConf&&<button className="clear-conf-btn" onClick={clearConfidence}><Close size={10} /> clear</button>}
              </label>
              {!hasConf?(
                <button className="set-conf-btn" onClick={()=>{setHasConf(true);autoSave(50,true,status,notes);}}>+ Set confidence</button>
              ):(
                <div className="slider-wrap">
                  <input type="range" min="0" max="100" step="5" value={conf} className="conf-slider"
                    onChange={e=>{setConf(parseInt(e.target.value));autoSave(e.target.value,true,status,notes);}}/>
                  <div className="slider-labels"><span>0</span><span>50</span><span>100</span></div>
                </div>
              )}
            </div>
          </div>
          <div className="field-group" style={{marginTop:12}}>
            <label className="field-label">Notes</label>
            <textarea className="field-notes" placeholder="e.g. Weak on s.423 IA 1986 — revisit. MCQs drilled ×2."
              value={notes} onChange={e=>{setNotes(e.target.value);autoSave(conf,hasConf,status,e.target.value);}}/>
          </div>

          {/* SRA Links for Solicitors Accounts topics */}
          {topic.topic && topic.topic.toLowerCase().includes('solicitor') && (
            <SRALinks />
          )}

          {/* MCQ History section */}
          <div className="history-section">
            <button className="history-toggle" onClick={()=>setShowMCQHistory(h=>!h)}>
              {showMCQHistory?<ChevronUp size={10} />:<ChevronDown size={10} />} MCQ results for this topic
            </button>
            {showMCQHistory&&<MCQHistory topicId={topic.id}/>}
          </div>

          <div className="history-section">
            <button className="history-toggle" onClick={()=>setShowHistory(h=>!h)}>
              {showHistory?<ChevronUp size={10} />:<ChevronDown size={10} />} Revision history
            </button>
            {showHistory&&<HistoryLog topicId={topic.id}/>}
          </div>

          <div className="save-status">
            {saving&&<span className="save-saving">Saving…</span>}
            {saved&&!saving&&<span className="save-done">Saved</span>}
            {!saving&&!saved&&topic.date_updated&&<span className="save-date">Updated {topic.date_updated}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function SubjectGroup({ subject, topics, onSave, forceOpen, moduleColor, focusTopicId, statuses }) {
  const [open, setOpen] = useState(true);
  const done = topics.filter(t=>t.confidence!==null).length;
  const avg = done>0?topics.filter(t=>t.confidence!==null).reduce((s,t)=>s+t.confidence,0)/done:null;
  const hasFocus = topics.some(t=>t.id===focusTopicId);
  useEffect(()=>{ if(hasFocus) setOpen(true); },[hasFocus]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  return (
    <div className="subject-group">
      <div className="subject-header" onClick={()=>setOpen(o=>!o)}>
        <span className="subject-name" style={{color:moduleColor}}>{subject}</span>
        <div className="subject-right">
          <span className="subject-count">{done}/{topics.length}</span>
          {avg!==null&&<span className="subject-avg" style={{color:confColor(avg)}}>avg {Math.round(avg*100)}%</span>}
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>
      {open&&(
        <div className="subject-topics">
          {topics.map(t=><TopicRow key={t.id} topic={t} onSave={onSave} forceOpen={null} moduleColor={moduleColor} isFocused={t.id===focusTopicId} statuses={statuses}/>)}
        </div>
      )}
    </div>
  );
}

export default function Topics({ module, focusTopicId, onFocusConsumed }) {
  const [topics, setTopics] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);

  useEffect(() => {
    api('/api/settings').then(s => {
      try {
        const custom = JSON.parse(s.custom_statuses || '[]');
        setStatuses([...DEFAULT_STATUSES, ...custom]);
      } catch { setStatuses(DEFAULT_STATUSES); }
    }).catch(() => {});
  }, []);

  const moduleColor = module==='FLK1'?'var(--flk1)':'var(--flk2)';
  const moduleAccent = module==='FLK1'?'rgba(59,130,246,0.3)':'rgba(139,92,246,0.3)';

  useEffect(()=>{
    setLoading(true); setTopics([]); setFilter('all'); setSearch('');
    api(`/api/topics/${module}`).then(data=>{setTopics(data);setLoading(false);});
  },[module]);

  useEffect(()=>{
    if(focusTopicId&&!loading){
      const timer=setTimeout(()=>onFocusConsumed&&onFocusConsumed(),1500);
      return()=>clearTimeout(timer);
    }
  },[focusTopicId,loading,onFocusConsumed]);

  const handleSave=useCallback((id,updates)=>{
    setTopics(prev=>prev.map(t=>t.id===id?{...t,...updates}:t));
  },[]);

  const filtered=topics.filter(t=>{
    const q=search.toLowerCase();
    const matchSearch=!q||(t.subtopic||'').toLowerCase().includes(q)||(t.topic||'').toLowerCase().includes(q)||(t.general_topic||'').toLowerCase().includes(q)||(t.detail||'').toLowerCase().includes(q);
    if(!matchSearch) return false;
    if(filter==='weak') return t.confidence!==null&&t.confidence<0.5;
    if(filter==='untouched') return t.confidence===null&&!t.status;
    if(filter==='strong') return t.confidence!==null&&t.confidence>=0.7;
    return true;
  });

  const counts={
    all:topics.length,
    weak:topics.filter(t=>t.confidence!==null&&t.confidence<0.5).length,
    untouched:topics.filter(t=>t.confidence===null&&!t.status).length,
    strong:topics.filter(t=>t.confidence!==null&&t.confidence>=0.7).length,
  };

  const grouped={};
  filtered.forEach(t=>{ const key=t.topic||'Other'; if(!grouped[key]) grouped[key]=[]; grouped[key].push(t); });
  const useGroups=filter==='all'&&!search;

  if(loading) return <div className="loading">Loading {module}…</div>;

  return (
    <div className="topics-page">
      <div className="module-header-bar" style={{borderColor:moduleAccent,background:moduleAccent.replace('0.3','0.06')}}>
        <span className="module-tag" style={{color:moduleColor}}>{module}</span>
        <span className="module-desc" style={{color:'var(--muted)'}}>
          {module==='FLK1'?'Business Law, Dispute Resolution, Contract, Tort, Legal System & Services':'Land Law, Wills, Trusts, Solicitors Accounts, Criminal'}
        </span>
      </div>

      <div className="topics-toolbar">
        <input className="search-input" placeholder="Search topics, subjects, keywords…" value={search} onChange={e=>setSearch(e.target.value)}/>
        {useGroups&&(
          <><button className="collapse-btn" onClick={()=>setGroupsOpen(true)}>Expand all</button>
          <button className="collapse-btn" onClick={()=>setGroupsOpen(false)}>Collapse all</button></>
        )}
      </div>

      <div className="filter-row">
        {[['all','All'],['weak','Needs work'],['untouched','Not started'],['strong','Strong']].map(([k,label])=>(
          <button key={k} className={`filter-btn ${filter===k?'active':''}`}
            style={filter===k?{background:moduleColor,borderColor:moduleColor}:{}}
            onClick={()=>setFilter(k)}>
            {label} <span className="filter-count">{counts[k]}</span>
          </button>
        ))}
      </div>

      {filtered.length===0&&<div className="empty">No topics match this filter</div>}

      {useGroups?(
        Object.entries(grouped).map(([subject,tops])=>(
          <SubjectGroup key={subject} subject={subject} topics={tops} onSave={handleSave}
            forceOpen={groupsOpen} moduleColor={moduleColor} focusTopicId={focusTopicId} statuses={statuses}/>
        ))
      ):(
        <div className="topic-list">
          {filtered.map(t=><TopicRow key={t.id} topic={t} onSave={handleSave} forceOpen={null} moduleColor={moduleColor} isFocused={t.id===focusTopicId} statuses={statuses}/>)}
        </div>
      )}
    </div>
  );
}
